import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import { FinanceFormTabsEnum } from '@/components/forms/finance-form/finance-form.type'
import { EmptyState, TransactionDrawerForm } from '@/components'
import { AddCircleOutlineIcon, Button, Text } from '@mercai/clever'
import { CardOrderCredit, Progress } from './components'
import { TransactionInfo } from '../transaction-info'
import { OrderFinanceDrawerForm } from '../order-finance-drawer-form'

import { QUERIES_ADMIN, useTransactionDelete } from '@/services'
import {
  formatCurrency,
  onConfirmDangerMessage,
  onSuccessMessage,
} from '@/helpers'

import { TabCreditProps } from './tab-credit.type'
import { ProfileProps } from '@/types'

import styles from './tab-credit.module.css'

export const TabCredit = ({ isLoading, saleOrder }: TabCreditProps) => {
  const { t } = useTranslation('screenOrderCenterPage')

  const queryClient = useQueryClient()

  const [isOpenFinanceDrawerForm, setIsOpenFinanceDrawerForm] = useState<
    keyof typeof FinanceFormTabsEnum | ''
  >('')

  const [isOpenTransactionDrawerForm, setIsOpenTransactionDrawerForm] =
    useState(false)

  const [transactionUuid, setTransactionUuid] = useState('')

  const { mutateAsync: onDeleteTransaction } = useTransactionDelete()

  const onHandleUpdateTransaction = (uuid: string) => {
    setTransactionUuid(uuid)
    setIsOpenTransactionDrawerForm(true)
  }

  const onHandleNewTransaction = () => {
    setTransactionUuid('')
    setIsOpenTransactionDrawerForm(true)
  }

  const onHandleDeleteTransaction = async (uuid: string) => {
    const confirm = await onConfirmDangerMessage(
      t('orderCenterShowTabClientDeleteTransactionConfirmTitle'),
      t('orderCenterShowTabClientDeleteTransactionConfirmDescription'),
      t('orderCenterShowTabClientDeleteTransactionConfirmButton'),
      t('orderCenterShowTabClientDeleteTransactionCancelButton'),
    )

    if (!confirm) return

    const response = await onDeleteTransaction(uuid)

    if (response.success) {
      onSuccessMessage(
        t('orderCenterShowTabClientDeleteTransactionSuccessTitle'),
        t('orderCenterShowTabClientDeleteTransactionSuccessDescription'),
      )

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.ORDER_CENTER.module],
      })
    } else {
      onSuccessMessage(
        t('orderCenterShowTabClientDeleteTransactionErrorTitle'),
        t('orderCenterShowTabClientDeleteTransactionErrorDescription'),
      )
    }
  }

  const totalPrice = saleOrder?.totalOrderPrice
    ? +saleOrder?.totalOrderPrice
    : 0
  const totalPaid = saleOrder?.creditOrders?.[0]?.amount
    ? +saleOrder?.creditOrders?.[0]?.amount
    : 0

  const calculatePercentagePaid = () => {
    const calculated = (totalPaid / totalPrice) * 100

    return isNaN(calculated) ? 0 : calculated
  }

  const percentagePaid = calculatePercentagePaid()

  return (
    <>
      <Progress percentage={percentagePaid} />

      <div className={styles.values}>
        <div>
          <Text>{t('orderCenterShowTabCreditTotalPaid')}</Text>
          <Text variant="title-medium">
            {formatCurrency(totalPaid, 'es-MX', 'USD')}
          </Text>
        </div>

        <div>
          <Text>{t('orderCenterShowTabCreditTotalPending')}</Text>
          <Text variant="title-medium">
            {formatCurrency(totalPrice - totalPaid, 'es-MX', 'USD')}
          </Text>
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.transactions}>
          <div className={styles['transactions-header']}>
            <Text variant="title-medium">
              {t('orderCenterShowTabClientDepositsTitle')}
            </Text>
            <Button variant="success-light" onClick={onHandleNewTransaction}>
              {t('orderCenterShowTabClientDepositsAddButton')}
              <AddCircleOutlineIcon />
            </Button>
          </div>

          {isLoading && (
            <div className={styles['transactions-content']}>
              <TransactionInfo isLoading />
            </div>
          )}

          {!isLoading && saleOrder && saleOrder?.transactions?.length > 0 && (
            <div className={styles['transactions-content']}>
              {saleOrder?.transactions?.map((transaction, index) => {
                return (
                  <TransactionInfo
                    key={index}
                    transaction={transaction}
                    orderReference={saleOrder?.reference}
                    isLoading={isLoading}
                    onUpdate={() => onHandleUpdateTransaction(transaction.uuid)}
                    {...(transaction.paymentSource === 'internal' && {
                      onDelete: () =>
                        onHandleDeleteTransaction(transaction.uuid),
                    })}
                  />
                )
              })}
            </div>
          )}

          {!isLoading &&
            (!saleOrder || saleOrder?.transactions?.length === 0) && (
              <EmptyState
                horizontal
                title={t('orderCenterShowTabClientEmptyTitle')}
                description={t('orderCenterShowTabClientEmptyDescription')}
              />
            )}
        </div>

        <div className={styles.sidebar}>
          <CardOrderCredit
            saleOrder={saleOrder}
            paymentMethod={saleOrder?.payments?.[0]?.form}
            totalOrderPrice={totalPrice}
            orderDate={saleOrder?.createdAt}
            isLoading={!!isLoading}
          />
        </div>
      </div>

      {saleOrder?.uuid && (
        <>
          <TransactionDrawerForm
            uuid={transactionUuid}
            orderUuid={saleOrder.uuid}
            payer={saleOrder?.buyer as unknown as ProfileProps}
            isOpen={isOpenTransactionDrawerForm}
            onHandleOpen={() => setIsOpenTransactionDrawerForm(false)}
            hasOrderStep={false}
          />
          <OrderFinanceDrawerForm
            initialValues={saleOrder}
            initialTab={isOpenFinanceDrawerForm || 'payments'}
            isOpen={!!isOpenFinanceDrawerForm}
            onHandleClose={() => setIsOpenFinanceDrawerForm('')}
          />
        </>
      )}
    </>
  )
}
